import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "@/lib/api";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft, HardHat } from "lucide-react";

const NICHES = [
  "Civil Construction", "Electrical", "Plumbing", "Road & Highway",
  "Building Renovation", "Water Supply", "Drainage", "Irrigation",
  "Government Furniture", "IT Infrastructure", "Other"
];

export default function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [workNiche, setWorkNiche] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // OTP step
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [resendTimer, setResendTimer] = useState(0);
  const otpRefs = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(""));
      otpRefs.current[5]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const res = await auth.login({ email, password });
        localStorage.setItem("contractor_os_token", res.data.token);
        localStorage.setItem("contractor_os_user", JSON.stringify(res.data.user || {}));
        toast.success("Welcome back!");
        navigate("/app/dashboard");
      } else {
        await auth.signup({ email, password, company_name: companyName, work_niche: workNiche });
        toast.success("Verification code sent to your email");
        setOtpStep(true);
        setResendTimer(60);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const code = otp.join("");
    if (code.length < 6) { toast.error("Enter the 6-digit code"); return; }
    setLoading(true);
    try {
      const res = await auth.verifyOtp({ email, otp: code });
      localStorage.setItem("contractor_os_token", res.data.token);
      localStorage.setItem("contractor_os_user", JSON.stringify(res.data.user || {}));
      toast.success("Account created! Welcome to ContractorOS");
      setTimeout(() => navigate("/app/dashboard"), 500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
      setOtp(["", "", "", "", "", ""]);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setLoading(true);
    try {
      await auth.signup({ email, password, company_name: companyName, work_niche: workNiche });
      toast.success("New code sent");
      setResendTimer(60);
      setOtp(["", "", "", "", "", ""]);
      otpRefs.current[0]?.focus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resend code");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-[#0A0A08] border border-[#27272A] text-[#F5F2E8] font-body px-4 py-3 focus:border-[#FF5C1A] focus:ring-1 focus:ring-[#FF5C1A] outline-none transition-colors placeholder:text-[#444]";

  // ── OTP screen ──
  if (otpStep) {
    return (
      <div className="min-h-screen bg-[#0A0A08] flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <button onClick={() => { setOtpStep(false); setOtp(["", "", "", "", "", ""]); }}
            className="flex items-center gap-2 text-[#888880] font-mono text-xs tracking-widest uppercase mb-10 hover:text-[#F5F2E8] transition-colors">
            <ArrowLeft size={14} /> Back
          </button>

          <div className="font-heading text-3xl tracking-[3px] mb-2">
            CONTRACTOR<span className="text-[#FF5C1A]">OS</span>
          </div>
          <p className="text-[#888880] text-sm mb-1">Verify your email</p>
          <p className="text-[#F5F2E8] text-sm mb-10">
            We sent a 6-digit code to <span className="text-[#FF5C1A]">{email}</span>
          </p>

          <form onSubmit={handleVerifyOtp} className="space-y-8">
            <div>
              <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-4">
                Verification Code
              </label>
              <div className="flex gap-3 justify-between" onPaste={handleOtpPaste}>
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => (otpRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    className="w-12 h-14 text-center text-xl font-bold bg-[#0A0A08] border border-[#27272A] text-[#F5F2E8] focus:border-[#FF5C1A] focus:ring-1 focus:ring-[#FF5C1A] outline-none transition-colors rounded"
                  />
                ))}
              </div>
              <p className="text-[10px] text-[#888880] font-mono mt-3">Code expires in 10 minutes</p>
            </div>

            <button type="submit" disabled={loading}
              className="w-full bg-[#FF5C1A] text-[#0A0A08] py-3.5 font-mono text-sm font-bold tracking-widest uppercase hover:bg-[#FFB800] transition-colors disabled:opacity-50 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.5)] active:translate-y-[2px] active:shadow-none">
              {loading ? "Verifying..." : "Verify & Create Account"}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button onClick={handleResend} disabled={resendTimer > 0 || loading}
              className="text-[#888880] font-mono text-xs tracking-widest hover:text-[#FF5C1A] transition-colors disabled:opacity-40">
              {resendTimer > 0 ? `RESEND CODE IN ${resendTimer}s` : "RESEND CODE"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Login / Signup screen ──
  return (
    <div className="min-h-screen bg-[#0A0A08] flex items-center justify-center px-4" data-testid="auth-page">
      <div className="w-full max-w-md">
        <button data-testid="back-to-home-btn" onClick={() => navigate("/")} className="flex items-center gap-2 text-[#888880] font-mono text-xs tracking-widest uppercase mb-10 hover:text-[#F5F2E8] transition-colors">
          <ArrowLeft size={14} /> Back to Home
        </button>

        <div className="font-heading text-3xl tracking-[3px] mb-2">
          CONTRACTOR<span className="text-[#FF5C1A]">OS</span>
        </div>
        <p className="text-[#888880] text-sm mb-10">
          {isLogin ? "Sign in to your command center" : "Create your contractor account"}
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          {!isLogin && (
            <>
              <div>
                <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-2">Company Name</label>
                <input data-testid="company-name-input" type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Environ Solutions Pvt Ltd" className={inputCls} />
              </div>
              <div>
                <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-2">
                  <HardHat size={12} className="inline mr-1 mb-0.5" /> Work Niche
                </label>
                <select data-testid="work-niche-select" value={workNiche} onChange={(e) => setWorkNiche(e.target.value)} className={inputCls}>
                  <option value="">Select your primary niche</option>
                  {NICHES.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                <p className="text-[10px] text-[#888880] mt-1 font-mono">Tenders will be filtered by your niche by default</p>
              </div>
            </>
          )}

          <div>
            <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-2">Email</label>
            <input data-testid="email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="contractor@company.com" className={inputCls} />
          </div>

          <div>
            <label className="block font-mono text-[10px] text-[#888880] tracking-[2px] uppercase mb-2">Password</label>
            <div className="relative">
              <input data-testid="password-input" type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" className={`${inputCls} pr-12`} />
              <button type="button" data-testid="toggle-password-btn" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#888880] hover:text-[#F5F2E8] transition-colors">
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button data-testid="auth-submit-btn" type="submit" disabled={loading} className="w-full bg-[#FF5C1A] text-[#0A0A08] py-3.5 font-mono text-sm font-bold tracking-widest uppercase hover:bg-[#FFB800] transition-colors disabled:opacity-50 shadow-[4px_4px_0px_0px_rgba(0,0,0,0.5)] active:translate-y-[2px] active:shadow-none">
            {loading ? "Please wait..." : isLogin ? "Sign In" : "Create Account"}
          </button>
        </form>

        <div className="mt-8 text-center">
          <button data-testid="toggle-auth-mode-btn" onClick={() => setIsLogin(!isLogin)} className="text-[#888880] font-mono text-xs tracking-widest hover:text-[#FF5C1A] transition-colors">
            {isLogin ? "Don't have an account? SIGN UP" : "Already have an account? SIGN IN"}
          </button>
        </div>
      </div>
    </div>
  );
}
